// Header on-shift toggle for shift-role accounts (logist_cn, logist_ru).
//
// @aiPurpose Render a compact pill-shaped toggle for the authenticated
//   user's `isOnShift` state in the topbar. Visible for any system shift
//   role (`isShiftRoleName`); absent for other roles. Tapping the toggle
//   issues `PATCH /api/users/me/shift`; the response carries the post-toggle
//   `isOnShift` flag and is patched onto `useAuthStore` directly so the
//   UI reflects the change without round-tripping `/auth/me`
//   (which is fetched imperatively in `useAuthBootstrap`, not via
//   React Query).
// @aiInvariant The button is disabled while the mutation is in-flight
//   so a double-tap cannot enqueue conflicting requests. On error the
//   user remains pinned to the pre-toggle state (the store is only
//   touched on success), so a backend FORBIDDEN/VALIDATION/CONFLICT
//   rejection never leaves the UI in a lying state. Errors are
//   surfaced via the shared `errorMessage` mapping + toast pipeline so
//   the operationally-critical `SHIFT_CANNOT_LEAVE_LAST` (409) and
//   forbidden/validation cases tell the user *why* the toggle did not
//   move. The non-shift-role short-circuit returns null so the
//   topbar layout shrinks naturally for admin/manager users. The
//   `isShiftRoleName` predicate is single-sourced in `packages/shared`
//   so backend last-on-shift guards and this UI gate stay in lockstep.
//
// WHY a stand-alone component (not inlined into Topbar): keeps the
// store-update + mutation wiring in one place, lets tests target the
// toggle without coupling to the topbar's other surfaces.

import { useTranslation } from 'react-i18next';
import { usePatchSelfShift } from '@magimp/shared-hooks';
import { isShiftRoleName } from '@magimp/shared';
import { useAuthStore } from '../../../stores/auth.js';
import { useToastStore } from '../../../stores/toast.js';
import { errorMessage } from '../../../api/errors.js';

export function OnShiftToggle() {
  const { t } = useTranslation('shell');
  const user = useAuthStore((s) => s.user);
  // INVARIANT (#440 Phase 3): the top-level slot is the only wire-fed
  //   source for the live shift flag (`UserSchema` no longer carries
  //   it). `MeResponseSchema.isOnShift` is the on-the-wire field; the
  //   slot mirrors it and `setUserShift` flips it in place after the
  //   PATCH /me/shift round-trip.
  const isOnShift = useAuthStore((s) => s.isOnShift);
  const setUserShift = useAuthStore((s) => s.setUserShift);
  const showToast = useToastStore((s) => s.show);
  const mutation = usePatchSelfShift();

  if (!user) return null;
  if (!user.role.isSystem || !isShiftRoleName(user.role.name)) return null;

  // INVARIANT: when the session is hydrated but the slot is still null
  //   (extremely brief race during bootstrap), default to `false` so the
  //   toggle renders off rather than crashing on a missing read. The
  //   next /auth/me round-trip reconciles.
  const checked = isOnShift ?? false;
  const pending = mutation.isPending;

  const onClick = () => {
    if (pending) return;
    // Capture both the user id AND the session revision at dispatch
    // (see `setUserShift`'s contract): a late response landing after a
    // logout / re-login must NOT corrupt the new session's slot. The
    // userId guard covers cross-user re-login; the sessionRevision
    // guard covers same-user re-login (logout → clear bumps the
    // revision, the new session starts at the bumped value).
    const requestUserId = user.id;
    const requestSessionRevision = useAuthStore.getState().sessionRevision;
    mutation.mutate(
      { isOnShift: !checked },
      {
        onSuccess: (data) => {
          setUserShift(requestUserId, requestSessionRevision, data.isOnShift);
        },
        onError: (err) => {
          showToast({ tone: 'error', text: errorMessage(err) });
        },
      },
    );
  };

  const ariaLabel = checked ? t('topbar.onShift.ariaOn') : t('topbar.onShift.ariaOff');

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={pending}
      onClick={onClick}
      data-testid="on-shift-toggle"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        height: 28,
        padding: '0 10px',
        borderRadius: 'var(--r-1)',
        border: '1px solid var(--border-1)',
        background: 'var(--bg-1)',
        color: 'var(--fg-3)',
        fontSize: 12,
        fontWeight: 500,
        cursor: pending ? 'progress' : 'pointer',
        opacity: pending ? 0.6 : 1,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 24,
          height: 14,
          borderRadius: 999,
          background: checked ? 'var(--accent-1)' : 'var(--border-2)',
          position: 'relative',
          transition: 'background var(--dur-1) var(--ease-out)',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 1,
            left: checked ? 11 : 1,
            width: 12,
            height: 12,
            borderRadius: '50%',
            background: 'var(--bg-1)',
            transition: 'left var(--dur-1) var(--ease-out)',
          }}
        />
      </span>
      <span>{t('topbar.onShift.label')}</span>
    </button>
  );
}
